const {React} = require('react');

/**
 * Function which dedupes contentful result lists and removes duplicate slugs and respects localization
 * Use spaceID to default to a particular space content; will fallback to global if no match occurs
 * Respects localizationSlug if associated with downstream edges and defaults to this
 * Not to be used if you have more than 2 geos together as you may default to the incorrect one
 * 
 * NOTE: Used when your query is structured as { edges { node {...} } }, pass in the edges list, if 
 * your query is structured as { nodes {...} }, use the other dedupe function below, dedupeLocalizedNodeList
 *
 * @function dedupeLocalizedList
 * @returns {jsx} The array of objects
 */
const dedupeLocalizedList = (list, spaceId = process.env.CONTENTFUL_GLOBAL_SPACE_ID) => {
  let key = "slug";
  let localizationKey = "localizationSlug";

  let edges = [];
  let keys = [];

  list.forEach(edge => {
    if(!keys.includes(edge.node[key]) && !keys.includes(edge.node[localizationKey])) {
      edges.push(edge);
      keys.push(edge.node[key]);
    } else {
      if(edge.node.spaceId === spaceId || (edge.node[localizationKey] !== null && keys.includes(edge.node[localizationKey]))) {
        let objectToReplace = edges.find(e => e.node[key] === edge.node[localizationKey])
        if(objectToReplace !== undefined) {
          Object.assign(objectToReplace, edge);
        }
      }
    }
  });

  return edges;
};

const dedupeListWithExclusionList = (list, space) => {
  list = list?.filter(industry => {
    let exclusionList = industry.exclusionList;

    if(space && exclusionList) {
      if(space === "en-gb" && !exclusionList.uk) {
        return industry
      } else if(space === "en-us" && !exclusionList.us) {
        return industry
      } else if(space === "de-de" && !exclusionList.germany) {
        return industry
      } else if(space === "en-au" && !exclusionList.australia) {
        return industry
      } else if(space === "en-in" && !exclusionList.india) {
        return industry
      }  
    } else {
      return industry
    }
  })

  return list;
};

const dedupeGeneratorList = (list) => {
  let key = "slug";
  let localizationKey = "localizationSlug";

  let contentEdges = [];
  let keys = [];
  let localizationMap = new Map();

  list.forEach(edge => {
    if(!keys.includes(edge.node[key]) && !keys.includes(edge.node[localizationKey])) {
      contentEdges.push(edge);
      keys.push(edge.node[key]);
    } else {
      if((keys.includes(edge.node[localizationKey]))) {
        contentEdges.push(edge);
        keys.push(edge.node[key]);

        if(localizationMap.has(edge.node[localizationKey])) {
          let existingSpaceList = localizationMap.get(edge.node[localizationKey]);
          existingSpaceList.push(edge.node.spaceId)
          localizationMap.set(edge.node[localizationKey], existingSpaceList)
        } else {
          localizationMap.set(edge.node[localizationKey], [edge.node.spaceId]);
        }
      }
    }

  });

  return {contentEdges, localizationMap};
};

const generateLocalizationMap = (nodes) => {
  let localizationKey = "localizationSlug";
  let localizationMap = new Map();

  nodes.forEach(node => {
    if(localizationMap.has(node[localizationKey])) {
      let existingSpaceList = localizationMap.get(node[localizationKey]);
      existingSpaceList.push(node.spaceId)
      localizationMap.set(node[localizationKey], existingSpaceList)
    } else {
      localizationMap.set(node[localizationKey], [node.spaceId]);
    }
  });

  return localizationMap;
};

/**
 * Function which dedupes contentful result lists and removes duplicate slugs
 * 
 * NOTE: Used when your query is structured as { nodes {...} }, pass in the nodes list, if your 
 * query is structured as { edges { node {...} } }, use the other dedupe function above, dedupeLocalizedList
 *
 * @function dedupeLocalizedNodeList
 * @returns {jsx} The array of objects
 */
const dedupeLocalizedNodeList = (list, spaceId = process.env.CONTENTFUL_GLOBAL_SPACE_ID) => {
  let key = 'slug';
  let localizationKey = 'localizationSlug';

  let nodes = [];
  let keys = [];

  list.forEach(node => {
    if (!keys.includes(node[key]) && !keys.includes(node[localizationKey])) {
      nodes.push(node);
      keys.push(node[key]);
    } else {
      if (node.spaceId === spaceId || (node[localizationKey] !== null && keys.includes(node[localizationKey]))) {
        let objectToReplace = nodes.find(n => n[key] === node[localizationKey]);
        if (objectToReplace !== undefined) {
          Object.assign(objectToReplace, node);
        }
      }
    }
  });

  return nodes;
};

/**
 * Function which removes geo specific industries from non-geo
 * @function dedupeGeoSpecificIndustries
 * @returns {jsx} The array of objects
 */
const dedupeGeoSpecificIndustries = (list, space) => {
  let industries = list;

  if(industries) {
    if(space === "en-gb") {
      industries = industries.filter(industry => !industry?.slug.includes("u-s-healthcare-payer-and-health-insurance"));
    } else if(space === "en-us") {
      industries = industries.filter(industry => !industry?.slug.includes("uk-public-healthcare"));
    } else if(space === "en-au" || space === "en-in" || space === "de-de") {
      industries = industries.filter(industry => !industry?.slug.includes("uk-public-healthcare") && !industry?.slug.includes("u-s-healthcare-payer-and-health-insurance"));
    }  
  }

  return industries;
};

/**
 * Function which removes geo specific industries from non-geo
 * @function dedupeGeoSpecificIndustries
 * @returns {jsx} The array of objects
 */
const dedupeGeoSpecificIndustriesBySpaceId = (list, spaceId) => {
  let industries = list;

  if(industries) {
    if(spaceId === process.env.CONTENTFUL_UK_SPACE_ID) {
      industries = industries.filter(industry => !industry?.slug.includes("u-s-healthcare-payer-and-health-insurance"));
    } else if(spaceId === process.env.CONTENTFUL_US_SPACE_ID) {
      industries = industries.filter(industry => !industry?.slug.includes("uk-public-healthcare"));
    } else if(spaceId === process.env.CONTENTFUL_AU_SPACE_ID || spaceId === process.env.CONTENTFUL_IN_SPACE_ID || spaceId === process.env.CONTENTFUL_DE_SPACE_ID) {
      industries = industries.filter(industry => !industry?.slug.includes("uk-public-healthcare") && !industry?.slug.includes("u-s-healthcare-payer-and-health-insurance"));
    }
  }

  return industries;
};


module.exports.dedupeLocalizedList = dedupeLocalizedList;
module.exports.dedupeListWithExclusionList = dedupeListWithExclusionList;
module.exports.dedupeGeneratorList = dedupeGeneratorList;
module.exports.dedupeLocalizedNodeList = dedupeLocalizedNodeList;
module.exports.generateLocalizationMap = generateLocalizationMap;
module.exports.dedupeGeoSpecificIndustries = dedupeGeoSpecificIndustries;
module.exports.dedupeGeoSpecificIndustriesBySpaceId = dedupeGeoSpecificIndustriesBySpaceId;
